<template>
  <div style="flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; overflow-y: auto; background: var(--surface)">
    <div header style="height: 56px; padding: 0 16px; gap: 12px">
      <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px">
        <div style="display: flex; align-items: center; gap: 8px; min-width: 0">
          <span ellipsis :title="headerTitle" style="font-size: 14.5px">{{ headerTitle }}</span>
          <span v-if="$store.state.currentClip.id && showsStatus" class="pill" :style="pillStyle" :title="state.detail"><span class="pill-dot"></span>{{ state.label }}</span>
        </div>
        <span class="mono" ellipsis :title="fileLine" style="font-size: 10px; font-weight: 400; color: var(--text-faint)">{{ fileLine }}</span>
      </div>
      <button
        type="button"
        class="mv-ghost"
        :class="{ 'is-on': inCart }"
        :disabled="!$store.state.currentClip.id"
        :title="$store.state.cartToggleTitle($store.state.currentClip.id)"
        @click="$store.state.toggleWholeAsset($store.state.currentClip)">
        {{ inCart ? "✓ In cart" : "+ Add to cart" }}
      </button>
    </div>
    <!-- video: a 16:9 box on the panel background, capped by the viewport height and by the open details -->
    <div style="flex: none; padding: 12px 16px 0">
      <div
        ref="videoBox"
        :style="{ width: videoWidth }"
        style="position: relative; aspect-ratio: 16 / 9; margin: 0 auto; border-radius: 6px; overflow: hidden; background: var(--bg)">
        <video
          ref="videoRef"
          :style="src && videoHasDuration ? '' : 'display: none !important'"
          :src="src"
          @timeupdate="onVideoTimeUdate"
          @durationchange="onVideoDurationChange"
          @play="playing = true"
          @pause="playing = false"
          @ended="playing = false"
          style="position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain"></video>
        <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; cursor: pointer" @click="togglePlay">
          <v-progress-circular v-if="src && !videoHasDuration" size="60" width="6" style="opacity: 0.5" indeterminate></v-progress-circular>
          <div
            v-else-if="!playing && !controlsDisabled"
            style="
              width: 56px;
              height: 56px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding-left: 3px;
              border: 1px solid rgba(255, 255, 255, 0.22);
              border-radius: 50%;
              background: rgba(14, 15, 18, 0.6);
              color: #fff;
              font-size: 19px;
            ">
            ▶︎
          </div>
        </div>
        <span
          v-if="techTag"
          class="mono"
          style="position: absolute; top: 10px; left: 10px; height: 20px; display: inline-flex; align-items: center; padding: 0 7px; border-radius: 4px; background: rgba(14, 15, 18, 0.72); font-size: 10px; color: #fff">
          {{ techTag }}
        </span>
      </div>
    </div>

      <!-- controls: scrubber, transport row, clip bar -->
      <div style="flex: none; padding: 12px 16px 14px; display: flex; flex-direction: column; gap: 12px">
        <div title="Scrub (← / → one frame, J / L 10 s)">
          <SliderComponent
            :duration="duration || 0"
            :time="currentTime"
            :range-in="markIn == null ? null : markIn / FPS"
            :range-out="markOut == null ? null : markOut / FPS"
            :disabled="controlsDisabled"
            @seek="seek"></SliderComponent>
        </div>

        <!-- transport: timecode | −10s play +10s | speed and panels -->
        <div style="display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 12px">
          <div style="display: flex; flex-direction: column; gap: 1px; min-width: 0; overflow: hidden">
            <input
              v-if="tcEditing"
              ref="tcInput"
              v-model="tcText"
              class="mono mv-tc"
              style="width: 11ch; padding: 0; border: 0; background: transparent"
              @keydown.enter="commitTc()"
              @keydown.esc="tcEditing = false"
              @blur="tcEditing = false" />
            <span v-else class="mono mv-tc" :title="tip('gotoTc')" style="cursor: text" @click="editTc()">{{ currentTc }}</span>
            <span class="mono" style="font-size: 10.5px; color: var(--text-faint); white-space: nowrap">of {{ totalTc }}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 4px">
            <button type="button" class="mv-tbtn mv-tbtn--mono" :title="tip('back10')" :disabled="controlsDisabled" @click="seekBy(-10)">−10s</button>
            <button type="button" class="mv-playbtn" :title="tip('play')" :disabled="controlsDisabled" @click="togglePlay()">{{ playing ? "❚❚" : "▶︎" }}</button>
            <button type="button" class="mv-tbtn mv-tbtn--mono" :title="tip('fwd10')" :disabled="controlsDisabled" @click="seekBy(10)">+10s</button>
          </div>
          <div style="display: flex; align-items: center; justify-content: flex-end; gap: 4px">
            <button
              type="button"
              class="mv-tbtn mv-tbtn--mono"
              style="min-width: 40px; padding: 0 8px; font-size: 11.5px"
              title="Playback speed"
              :disabled="controlsDisabled"
              @click="cycleSpeed()">
              {{ speed }}×
            </button>
            <button
              type="button"
              class="mv-tbtn"
              :class="{ 'is-on': detailsOpen }"
              style="padding: 0 10px"
              :title="tip('metadata')"
              :disabled="detailsDisabled"
              @click="detailsOpen = !detailsOpen">
              Details <span style="font-size: 9px">{{ detailsOpen ? "▴" : "▾" }}</span>
            </button>
          </div>
        </div>

        <!-- clip bar: IN and OUT cells, name, clear, Add clip -->
        <div class="mv-field mv-field--bar">
          <button type="button" class="mv-markcell" :class="{ 'is-set': markIn != null }" :title="tip('markIn')" :disabled="controlsDisabled" @click="setMarkIn()">
            <span class="mono" style="font-size: 9.5px; letter-spacing: 0.08em; color: var(--accent)">IN</span>
            <span class="mono" :style="{ fontSize: '11px', color: markIn != null ? 'var(--accent-text)' : 'var(--text-ph)' }">{{ inTc }}</span>
          </button>
          <button type="button" class="mv-markcell" :class="{ 'is-set': markOut != null }" :title="tip('markOut')" :disabled="controlsDisabled" @click="setMarkOut()">
            <span class="mono" style="font-size: 9.5px; letter-spacing: 0.08em; color: var(--accent)">OUT</span>
            <span class="mono" :style="{ fontSize: '11px', color: markOut != null ? 'var(--accent-text)' : 'var(--text-ph)' }">{{ outTc }}</span>
          </button>
          <input
            v-model="name"
            :placeholder="namePlaceholder"
            :disabled="controlsDisabled"
            style="flex: 1; min-width: 60px; padding: 0 12px; font-size: 12.5px"
            @keydown.enter="addClip()" />
          <button v-if="hasMarks" type="button" class="mv-clipbar-clear" :title="tip('clearMarks')" aria-label="Clear In/Out" @click="clearMarks()">✕</button>
          <button
            type="button"
            class="mv-primary"
            :title="tip('addSubclip')"
            :disabled="controlsDisabled"
            style="height: auto; align-self: stretch; margin: 3px; border-radius: 6px; gap: 8px; font-size: 12.5px"
            @click="addClip()">
            Add clip<span class="mono" style="font-size: 10.5px; font-weight: 500; color: var(--accent-text)">{{ selectionTc }}</span>
          </button>
        </div>

        <ClipDetails ref="details" v-show="detailsOpen" :asset="$store.state.currentClip" :duration="duration || 0" :tab.sync="detailsTab" v-model="note" />
      </div>
  </div>
</template>

<script>
import { EventBus } from "@/scripts/event-bus.js";
import { assetState, showsStatus, hasProxy, pillStyle, assetKind } from "@/scripts/assetStatus";
import { FPS, WIRE_UNSET, UNSET_TC, toFrames, framesToTc, toTc, parseTc, offsetFrames } from "@/scripts/timecode.js";
import { tip, shortcutFor, isTypingTarget, isOverlayOpen } from "@/scripts/shortcuts.js";
import { assetTitle, assetFilename, codecLabel } from "@/scripts/assetLabels.js";

// The speed button cycles through these; each asset load starts at 1×.
const SPEEDS = [1, 2, 4, 0.5];
// The open details shrink the video to fit, but not below this height (px); below it the pane scrolls.
const VIDEO_FLOOR = 120;

export default {
  data: () => {
    return {
      FPS,
      speed: 1,
      tcEditing: false,
      tcText: "",
      currentTime: 0,
      duration: 0,
      src: "",
      loadedId: undefined,
      pendingSeek: null,
      playing: false,
      // file-time frames; null = unset
      markIn: null,
      markOut: null,
      note: "",
      name: "",
      detailsOpen: false,
      detailsTab: "meta",
      // px height the video may take while the details are open; null = its full size
      videoCap: null,
    };
  },
  methods: {
    tip,
    onKey(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target) || isOverlayOpen(this.$store.state)) return;
      const shortcut = shortcutFor(event);
      if (!shortcut || (shortcut.anyAsset ? this.detailsDisabled : this.controlsDisabled)) return;
      event.preventDefault();
      if (event.repeat && !shortcut.repeat) return;
      // a focused button would also fire its click on the Space keyup
      if (event.key === " " && document.activeElement && document.activeElement.tagName === "BUTTON") document.activeElement.blur();
      this[shortcut.method](shortcut.arg);
    },
    /** Swaps the timecode readout for a field; Enter jumps there, Esc or leaving the field cancels. */
    editTc() {
      if (this.controlsDisabled) return;
      this.tcText = this.currentTc;
      this.tcEditing = true;
      this.$nextTick(() => this.$refs.tcInput && this.$refs.tcInput.select());
    },
    commitTc() {
      this.seekToTc(this.tcText);
      this.tcEditing = false;
    },
    cycleSpeed() {
      this.speed = SPEEDS[(SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length];
      this.$refs.videoRef.playbackRate = this.speed;
    },
    // While playing, the readout follows every frame instead of timeupdate's 4 Hz.
    followPlayhead() {
      if (!this.playing) return;
      const time = this.$refs.videoRef.currentTime;
      if (toFrames(time) !== toFrames(this.currentTime)) this.currentTime = time;
      this.frameRequest = requestAnimationFrame(this.followPlayhead);
    },
    /** Shrinks the video by what the open details overflow; measured from full size so it can grow back. */
    fitVideo() {
      this.videoCap = null;
      if (!this.detailsOpen) return;
      this.$nextTick(() => {
        const overflow = this.$el.scrollHeight - this.$el.clientHeight;
        if (overflow > 0) this.videoCap = Math.max(VIDEO_FLOOR, this.$refs.videoBox.offsetHeight - overflow);
      });
    },
    /** M: Details on the Metadata tab, or closes it when that tab is already showing. */
    toggleMetadata() {
      this.detailsOpen = !(this.detailsOpen && this.detailsTab === "meta");
      this.detailsTab = "meta";
    },
    /** N: Details on the Comment tab, ready to type. */
    openComment() {
      this.detailsOpen = true;
      this.detailsTab = "comment";
      this.$refs.details.focusComment();
    },
    /** Adds the marked clip to the active cart; the draft clears only once the add succeeded. */
    addClip() {
      const state = this.$store.state;
      if (!state.currentClip.id) return;
      const subclip = {
        id: state.getRandomUuid(),
        assetID: state.currentClip.id,
        cartID: state.cart.id,
        cartName: state.cart.name,
        name: this.name.trim() || assetTitle(state.currentClip),
        note: this.note,
        subclipStatus: "NEW",
        subclipPriority: "MEDIUM",
        ...this.subclipTimecodes(),
      };
      state.addSubclip(subclip, (ok) => ok && this.clearDraft());
    },
    clearDraft() {
      this.name = "";
      this.note = "";
      this.clearMarks();
    },
    /** No-op without media: play() on an empty source rejects with "no supported sources". */
    togglePlay() {
      if (this.controlsDisabled) return;
      const video = this.$refs.videoRef;
      if (!video.paused) return video.pause();
      if (video.ended) this.seek(0);
      video.play();
    },
    pause() {
      this.$refs.videoRef.pause();
    },
    seekBy(secs) {
      this.seek(this.$refs.videoRef.currentTime + secs);
    },
    /** Pauses, then lands on the centre of the next or previous frame. */
    stepFrame(direction) {
      this.pause();
      const frame = Math.min(Math.max(toFrames(this.$refs.videoRef.currentTime) + direction, 0), Math.max(this.durationFrames - 1, 0));
      this.seek((frame + 0.5) / FPS);
    },
    /** The only writer of video.currentTime; clamps to the media. */
    seek(secs) {
      const time = Math.min(Math.max(secs, 0), this.duration || 0);
      this.currentTime = this.$refs.videoRef.currentTime = time;
    },
    /** Jumps to a source timecode; text that is not a whole timecode leaves the playhead alone. */
    seekToTc(text) {
      const secs = parseTc(text);
      if (secs == null) return;
      this.seek((toFrames(secs) - this.offset) / FPS);
    },
    onVideoTimeUdate(e) {
      this.currentTime = e.target.currentTime;
      this.duration = e.target.duration;
    },
    onVideoDurationChange(e) {
      this.currentTime = e.target.currentTime;
      this.duration = e.target.duration;
      // an asset without a proxy plays the server's dummy file, whose length must not stick
      if (hasProxy(this.$store.state.currentClip)) this.$store.state.rememberDuration(this.$store.state.currentClip.id, this.duration);
      if (this.pendingSeek != null && this.duration > 0) {
        this.seek(this.pendingSeek);
        this.pendingSeek = null;
      }
    },
    /** The frame on screen: currentTime from timeupdate lags about 250 ms. */
    playheadFrame() {
      return Math.min(Math.max(toFrames(this.$refs.videoRef.currentTime), 0), this.durationFrames);
    },
    // In stays at least one frame before Out, and Out one after In.
    setMarkIn() {
      const frame = this.playheadFrame();
      this.markIn = this.markOut == null ? frame : Math.max(0, Math.min(frame, this.markOut - 1));
    },
    setMarkOut() {
      const frame = this.playheadFrame();
      this.markOut = this.markIn == null ? frame : Math.min(this.durationFrames, Math.max(frame, this.markIn + 1));
    },
    clearMarks() {
      this.markIn = null;
      this.markOut = null;
    },
    /** Source timecodes for the payload; an unset mark goes out as the wire sentinel. */
    subclipTimecodes() {
      const wire = (mark) => (mark == null ? WIRE_UNSET : framesToTc(mark + this.offset));
      // Out only: the clip starts at the asset's start, else the server computes a negative in-point
      const markIn = this.markIn == null && this.markOut != null ? 0 : this.markIn;
      return { tcIn: wire(markIn), tcOut: wire(this.markOut) };
    },
    /** A stored source timecode as a file-time mark; the sentinel is no mark. */
    markFromTc(tc) {
      const secs = parseTc(tc);
      return secs == null ? null : Math.max(0, toFrames(secs) - this.offset);
    },
    /** Every load starts an empty clip draft; the media itself reloads only for another asset. */
    loadMedia(asset) {
      const video = this.$refs.videoRef;
      video.pause();
      // by id, not url: the url carries the Keycloak token, which refreshes every minute
      if (asset.id !== this.loadedId) {
        this.loadedId = asset.id;
        this.src = asset.id ? this.$store.state.getProxyUrl(asset.id) : "";
        this.duration = 0;
      } else {
        this.seek(0);
      }
      this.currentTime = 0;
      this.pendingSeek = null;
      this.speed = 1;
      video.playbackRate = 1;
      this.clearDraft();
    },
    // A cart clip pre-fills the draft, so a re-cut keeps its name, comment and marks.
    onPlaySubclip(subclip) {
      const loaded = this.loadedId === this.$store.state.currentClip.id;
      this.loadMedia(this.$store.state.currentClip);
      this.note = subclip.note || "";
      this.name = subclip.name;
      this.markIn = this.markFromTc(subclip.tcIn);
      this.markOut = this.markFromTc(subclip.tcOut);
      const start = (this.markIn ?? 0) / FPS;
      // another asset has no length yet, so the seek waits for its durationchange
      if (loaded) this.seek(start);
      else this.pendingSeek = start;
    },
    onPlayClip(clip) {
      this.loadMedia(clip);
    },
    onResetClip() {
      this.loadMedia({});
      this.$store.state.currentClip = {};
      this.$store.state.currentSubclip = {};
    },
  },
  computed: {
    state() {
      return assetState(this.$store.state.currentClip);
    },
    inCart() {
      return !!this.$store.state.wholeClipOf(this.$store.state.currentClip.id);
    },
    showsStatus() {
      return showsStatus(this.$store.state.currentClip);
    },
    pillStyle() {
      return pillStyle(this.state.color);
    },
    // the id check matters: assetTitle({}) is "Untitled"
    headerTitle() {
      const clip = this.$store.state.currentClip;
      return clip.id ? assetTitle(clip) : "No asset selected";
    },
    /** The raw filename under the title, or the TapeID when there is none. */
    fileLine() {
      const clip = this.$store.state.currentClip;
      return clip.id ? assetFilename(clip) || clip.tapeID || "" : "";
    },
    // ponytail: size and codec only; the API has no fps or scan type to add
    techTag() {
      const clip = this.$store.state.currentClip;
      if (!clip.id) return "";
      const codec = codecLabel(clip);
      if (assetKind(clip) === "AUDIO" || !(clip.width && clip.height)) return codec;
      return clip.width + "×" + clip.height + (codec ? " · " + codec : "");
    },
    namePlaceholder() {
      const clip = this.$store.state.currentClip;
      return clip.id ? "Clip name — " + assetTitle(clip) : "Clip name";
    },
    /** The asset's source timecode start, in frames. */
    offset() {
      return offsetFrames(this.$store.state.currentClip);
    },
    durationFrames() {
      return this.videoHasDuration ? toFrames(this.duration) : 0;
    },
    currentTc() {
      return framesToTc(toFrames(this.currentTime) + this.offset);
    },
    totalTc() {
      return toTc(this.duration || 0);
    },
    inTc() {
      return this.markIn == null ? UNSET_TC : framesToTc(this.markIn + this.offset);
    },
    outTc() {
      return this.markOut == null ? UNSET_TC : framesToTc(this.markOut + this.offset);
    },
    /** The clip length; an open end runs to the end of the asset. */
    selectionTc() {
      return framesToTc((this.markOut ?? this.durationFrames) - (this.markIn ?? 0));
    },
    hasMarks() {
      return this.markIn != null || this.markOut != null;
    },
    /** The one rule for the transport: buttons, fields and the keyboard shortcuts all obey it. */
    controlsDisabled() {
      // An uploaded asset has no tape, so what decides is whether a clip is loaded at all.
      return !this.$store.state.currentClip.id || !this.videoHasDuration;
    },
    // metadata needs no proxy, so an asset still processing can be described too
    detailsDisabled() {
      return !this.$store.state.currentClip.id;
    },
    videoWidth() {
      const cap = this.videoCap == null ? "" : ", " + Math.round((this.videoCap * 16) / 9) + "px";
      return "min(100%, calc(36vh * 16 / 9)" + cap + ")";
    },
    videoHasDuration() {
      return !isNaN(this.duration) && this.duration && this.duration > 0;
    },
  },
  mounted() {
    // the pane (window, splitter) and the details (tabs, editing) both change what has to fit
    this.fitObserver = new ResizeObserver(() => this.fitVideo());
    this.fitObserver.observe(this.$el);
    this.fitObserver.observe(this.$refs.details.$el);
    window.addEventListener("keydown", this.onKey);
    EventBus.$on("play-subclip", this.onPlaySubclip);
    EventBus.$on("play-clip", this.onPlayClip);
    EventBus.$on("reset-clip", this.onResetClip);
  },
  watch: {
    playing(now) {
      if (now) this.followPlayhead();
    },
  },
  beforeDestroy() {
    this.fitObserver.disconnect();
    cancelAnimationFrame(this.frameRequest);
    window.removeEventListener("keydown", this.onKey);
    EventBus.$off("play-clip", this.onPlayClip);
    EventBus.$off("reset-clip", this.onResetClip);
    EventBus.$off("play-subclip", this.onPlaySubclip);
  },
};
</script>
